import { CompleteTask } from "../CompleteTask/CompleteTask";
import { checkReservationExists, checkWorker, getReservationsByTask, isCallTask } from "../../ActionUtils";
import { WrapUpTask } from "../WrapUpTask/WrapUpTask";
import { ErrorCode, ErrorSeverity } from "~/modules/error";
import { getLogger, LoggerName } from "~/modules/logger";
import { TaskRouterImpl } from "~/packages/taskrouter/TaskRouterImpl";
import { CbmImpl } from "~/modules/cbm";
import { ContextInstance, ContextManager } from "~/modules/contextManager/ContextManager";
import { sendWorkerTrackingEvent } from "~/modules/telemetry/utils";
import { EVENTS } from "~/modules/telemetry/Analytics";
import { AnalyticsImpl } from "~/modules/telemetry/AnalyticsImpl";
import { Action, TaskReservation } from "~/modules/actions/Action";
import { createFlexSdkError } from "~/modules/error/ThrowError/ErrorHelper";





























export class EndTask implements Action<Promise<TaskReservation>> {
    


    readonly #taskSid: string;

    constructor(taskSid: string) {
        this.#taskSid = taskSid;
    }

    async run(ctx: {}) {
        const context = ctx as ContextManager;
        const taskRouter = context.retrieveInstanceOf(ContextInstance.TaskRouter, TaskRouterImpl);
        await taskRouter.init();
        const logger = getLogger(context)(LoggerName.Actions);
        const worker = taskRouter.worker;
        const analytics = context.retrieveInstanceOf(ContextInstance.AnalyticsImpl, AnalyticsImpl);
        const cbm = context.retrieveInstanceOf(ContextInstance.CbmImpl, CbmImpl);
        await cbm.init();

        logger.debug(`endTask invoked with taskSid: ${this.#taskSid}`);

        checkWorker(worker, {
            actionName: "EndTask",
            moduleName: "actions/Task",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.EndTaskCompleted,
            extra: {
                taskSid: this.#taskSid
            }
        });

        const reservations = getReservationsByTask(worker, this.#taskSid);
        const reservation = reservations.find((res) => res.status === "accepted");
        const wrappingReservation = reservations.find((res) => res.status === "wrapping");

        checkReservationExists(reservation ?? wrappingReservation, this.#taskSid, {
            actionName: "EndTask",
            moduleName: "actions/Task",
            analytics,
            context,
            worker,
            logger,
            event: EVENTS.EndTaskCompleted,
            extra: {
                taskSid: this.#taskSid
            }
        });

        try {
            if (reservation && isCallTask(reservation.task)) {
                const updatedReservation = await cbm.endConference(reservation);

                sendWorkerTrackingEvent(EVENTS.EndTaskCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: reservation.sid
                });
                return { task: updatedReservation.task, reservation: updatedReservation };
            }
            if (wrappingReservation) {
                const completedTask = await new CompleteTask(this.#taskSid).run(ctx);

                sendWorkerTrackingEvent(EVENTS.EndTaskCompleted, analytics, worker, {
                    taskSid: this.#taskSid,
                    reservationSid: wrappingReservation.sid
                });
                return Promise.resolve({ task: completedTask, reservation: wrappingReservation });
            }
            const { task, reservation: wrappedReservation } = await new WrapUpTask(this.#taskSid).run(ctx);

            sendWorkerTrackingEvent(EVENTS.EndTaskCompleted, analytics, worker, {
                taskSid: this.#taskSid,
                reservationSid: wrappedReservation.sid
            });

            return Promise.resolve({ task, reservation: wrappedReservation });
        } catch (error) {
            const errorMsg = `EndTask: Failed to end task ${this.#taskSid}`;
            sendWorkerTrackingEvent(EVENTS.EndTaskCompleted, analytics, worker, {
                error: errorMsg,
                taskSid: this.#taskSid,
                reservationSid: reservation?.sid ?? wrappingReservation?.sid
            });
            return Promise.reject(
                createFlexSdkError(context)(
                    ErrorCode.FailedToEndTask,
                    { severity: ErrorSeverity.Error },
                    errorMsg,
                    error
                )
            );
        }
    }
}
